import { observer } from 'mobx-react';
import { FC } from 'react';

import { Banner, Button, Form, Grid, Stack, Text } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { PhoneNumberInput } from '@servicetitan/form';

import { UserStore, Permissions } from '@servicetitan/application-data/user';
import { FeaturesStore } from '@servicetitan/application-data/features';
import { IsDialpadAdminStore } from '@servicetitan/application-data/telecom';
import { DefaultNumbersStore } from '../stores/default-numbers.store';
import { PlayGreetingStore } from '../stores/play-greeting.store';
import * as Styles from './default-numbers.module.less';
import { PlayGreeting as PlayGreetingComponent } from './play-greeting';

export const DefaultNumbers: FC = observer(() => {
    const [store, playGreetingStore, { hasPermission }, { isDialpadAdmin }, { features }] =
        useDependencies(
            DefaultNumbersStore,
            PlayGreetingStore,
            UserStore,
            IsDialpadAdminStore,
            FeaturesStore
        );
    const { callContinuityNumber, defaultSmsNumber, defaultMmsNumber } = store.formState.$;
    const { playGreeting } = playGreetingStore.formState.$;

    const canEditCallContinuityNumber =
        isDialpadAdmin || hasPermission(Permissions.UpdateForwardCallsToNumberOnSettingsPhone);
    const canEditDefaultNumber = hasPermission(Permissions.EditPhone);

    const isThirdPartyPhoneSystemEnabled =
        features.EnableServiceTitanPhonesUser || features.EnableContactCenterPro;
    const width = isThirdPartyPhoneSystemEnabled ? 4 : 3;

    return (
        <Form className={Styles.defaultNumbersForm} onSubmit={store.saveDefaultNumbers}>
            {(callContinuityNumber.hasError ||
                defaultSmsNumber.hasError ||
                defaultMmsNumber.hasError ||
                playGreeting.hasError) && (
                <Banner icon status="critical" title="Please correct the following errors:">
                    <ul>
                        {callContinuityNumber.hasError && (
                            <li>
                                {isThirdPartyPhoneSystemEnabled
                                    ? 'Call Continuity '
                                    : 'Forwarding '}
                                Number: {callContinuityNumber.error}
                            </li>
                        )}
                        {defaultSmsNumber.hasError && (
                            <li>Outbound SMS: {defaultSmsNumber.error}</li>
                        )}
                        {defaultMmsNumber.hasError && (
                            <li>Outbound MMS: {defaultMmsNumber.error}</li>
                        )}
                        {playGreeting.hasError && <li>Play Greeting: {playGreeting.error}</li>}
                    </ul>
                </Banner>
            )}
            <Grid className="m-y-2">
                <Grid.Column width={width}>
                    <PhoneNumberInput
                        size="large"
                        placeholder="(123) 456-7890"
                        sip={features.EnableSipNumbers}
                        label={
                            <Text className="no-wrap" el="span" size={2} bold>
                                {`Set ${
                                    isThirdPartyPhoneSystemEnabled
                                        ? 'Call Continuity Number'
                                        : 'Forwarding Number'
                                }`}
                            </Text>
                        }
                        value={callContinuityNumber.value || ''}
                        onChange={callContinuityNumber.onChangeNativeHandler}
                        error={callContinuityNumber.hasError}
                        onFocus={store.enableCallContinuityField}
                        onBlur={store.disableCallContinuityField}
                        disabled={!canEditCallContinuityNumber}
                        readOnly={
                            callContinuityNumber.value &&
                            !callContinuityNumber.dirty &&
                            store.isCallContinuityNumberFieldDisabled
                        }
                    />
                </Grid.Column>
                <Grid.Column width={width}>
                    <PhoneNumberInput
                        size="large"
                        placeholder="(123) 456-7890"
                        label={
                            <Text el="span" size={2} bold>
                                Outbound SMS
                            </Text>
                        }
                        value={defaultSmsNumber.value || ''}
                        onChange={defaultSmsNumber.onChangeNativeHandler}
                        error={defaultSmsNumber.hasError}
                        onFocus={store.enableDefaultSmsNumberField}
                        onBlur={store.disableDefaultSmsNumberField}
                        disabled={!canEditDefaultNumber}
                        readOnly={
                            defaultSmsNumber.value &&
                            !defaultSmsNumber.dirty &&
                            store.isDefaultSmsNumberFieldDisabled
                        }
                    />
                </Grid.Column>
                <Grid.Column width={width}>
                    <PhoneNumberInput
                        size="large"
                        placeholder="(123) 456-7890"
                        label={
                            <Text el="span" size={2} bold>
                                Outbound MMS
                            </Text>
                        }
                        value={defaultMmsNumber.value || ''}
                        onChange={defaultMmsNumber.onChangeNativeHandler}
                        error={defaultMmsNumber.hasError}
                        onFocus={store.enableDefaultMmsNumberField}
                        onBlur={store.disableDefaultMmsNumberField}
                        disabled={!canEditDefaultNumber}
                        readOnly={
                            defaultMmsNumber.value &&
                            !defaultMmsNumber.dirty &&
                            store.isDefaultMmsNumberFieldDisabled
                        }
                    />
                </Grid.Column>
                {!isThirdPartyPhoneSystemEnabled && (
                    <Grid.Column width={width}>
                        <PlayGreetingComponent
                            disabled={!canEditDefaultNumber}
                            displayLabel
                            store={playGreetingStore}
                        />
                    </Grid.Column>
                )}
            </Grid>
            {store.defaultNumbersFormHasChanges && (
                <Stack spacing={2}>
                    <Button
                        large
                        primary
                        width="89px"
                        type="sumbit"
                        disabled={store.isActionButtonsDisabled}
                    >
                        Save
                    </Button>
                    <Button
                        large
                        width="89px"
                        onClick={store.resetDefaultNumbers}
                        disabled={store.isActionButtonsDisabled}
                    >
                        Cancel
                    </Button>
                </Stack>
            )}
        </Form>
    );
});
